import { ticketType } from "@agentcall/core/type"
import { useEffect, useRef, useState } from "react"
import type { TicketGet200TicketStatus } from "@/api/client/ticket"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea/textarea"

interface StatusChangeDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  currentStatus: TicketGet200TicketStatus | null | undefined
  onSave?: (data: StatusChangeData) => void
}

export interface StatusChangeData {
  newStatus: TicketGet200TicketStatus
  reason: string
}

const statusOptions: { value: TicketGet200TicketStatus; label: string }[] =
  ticketType.TICKET_STATUSES.map((status) => ({
    value: status.key as TicketGet200TicketStatus,
    label: status.value,
  }))

const getStatusLabel = (status: string | null | undefined): string => {
  if (!status) return "未設定"
  const statusItem = ticketType.TICKET_STATUSES.find((item) => item.key === status)
  return statusItem?.value || status
}

const getReasonPlaceholder = (status: TicketGet200TicketStatus | ""): string => {
  switch (status) {
    case "owner_in_progress":
      return "こちらで対応します。"
    case "contractor_in_progress":
      return "業者手配したので、業者対応中とします。"
    case "completed":
      return "業者から対応完了報告をいただいたので、完了とします。"
    default:
      return ""
  }
}

export function StatusChangeDialog({
  open,
  onOpenChange,
  currentStatus,
  onSave,
}: StatusChangeDialogProps) {
  const reasonTextareaRef = useRef<HTMLTextAreaElement>(null)
  const selectTriggerRef = useRef<HTMLButtonElement>(null)
  const [formData, setFormData] = useState<{
    newStatus: TicketGet200TicketStatus | ""
    reason: string
  }>({
    newStatus: "",
    reason: "",
  })

  useEffect(() => {
    if (!open) return
    const timer = setTimeout(() => {
      if (!formData.newStatus) {
        selectTriggerRef.current?.focus()
      } else {
        reasonTextareaRef.current?.focus()
      }
    }, 100)
    return () => clearTimeout(timer)
  }, [open, formData.newStatus])

  const handleStatusChange = (value: TicketGet200TicketStatus) => {
    setFormData({
      ...formData,
      newStatus: value,
    })
  }

  const handleSave = () => {
    if (formData.newStatus && formData.reason) {
      onSave?.({
        newStatus: formData.newStatus,
        reason: formData.reason,
      })
      onOpenChange(false)
      // フォームをリセット
      setFormData({
        newStatus: "",
        reason: "",
      })
    }
  }

  const isFormValid = formData.newStatus && formData.reason.trim()

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[345px] p-6 rounded-lg">
        <DialogHeader className="text-center">
          <DialogTitle className="text-base font-semibold text-slate-900">
            ステータスを変更します
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1">
            <Label className="text-sm font-semibold text-slate-600">現在のステータス</Label>
            <div className="text-sm font-semibold text-slate-400">
              {getStatusLabel(currentStatus)}
            </div>
          </div>

          <div className="space-y-1">
            <Label htmlFor="newStatus" className="text-sm font-semibold text-slate-600">
              ステータス変更後
            </Label>
            <Select value={formData.newStatus} onValueChange={handleStatusChange}>
              <SelectTrigger ref={selectTriggerRef} className="h-10 text-sm">
                <SelectValue placeholder="選択してください" />
              </SelectTrigger>
              <SelectContent>
                {statusOptions
                  .filter((option) => option.value !== currentStatus)
                  .map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <Label htmlFor="reason" className="text-sm font-semibold text-slate-600">
              変更理由
            </Label>
            <Textarea
              ref={reasonTextareaRef}
              id="reason"
              value={formData.reason}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                setFormData({ ...formData, reason: e.target.value })
              }
              className="min-h-[80px] text-sm resize-none"
              placeholder={getReasonPlaceholder(formData.newStatus)}
            />
          </div>
        </div>

        <div className="flex flex-col gap-3 pt-3">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="w-full">
            キャンセル
          </Button>
          <Button onClick={handleSave} disabled={!isFormValid} className="w-full">
            変更する
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
